/**
 * プラン詳細画面の下段リスト部分のContainer Component
 */
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { GlossaryState } from 'common/modules/app/glossaries';
import {
  LOGIN_USER,
  UserInformation,
  getLogin
} from 'common/modules/domain/user';
import { PlanInformation } from 'common/modules/domain/plan';
import { Principal } from 'common/modules/app/principal';
import { PurchaseSessionState } from 'common/modules/app/purchaseSession';
import { Glossary } from 'common/utils/glossary';
import { AppState } from 'pages';
import {
  loadCookie,
  saveCookie,
  SasCookieKeys,
  deleteCookie
} from 'common/utils/cookie-utils';
import {
  POST_PURCHASE_SESSION_ID,
  postPurchaseSession
} from 'common/modules/domain/purchaseSession';

import { Services } from '../components/Services';
import { FetchState, FetchStatus } from 'common/modules/app/fetch';

interface OwnProps {
  targetPlan: PlanInformation;
}

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const userState = useSelector<AppState, UserInformation>(
    (state) => state.domain.user
  );
  const principalState = useSelector<AppState, Principal>(
    (state) => state.app.principal
  );
  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );
  const purchaseSessionState = useSelector<AppState, PurchaseSessionState>(
    (state) => state.app.purchaseSession
  );
  const isProcessing =
    fetchState[LOGIN_USER] == FetchStatus.PROCESSING ||
    fetchState[POST_PURCHASE_SESSION_ID] == FetchStatus.PROCESSING;
  // 購入開始処理中のプランIDの取得
  let processingPlanId = purchaseSessionState.planId;
  if (!processingPlanId) {
    // 購入開始処理中のプランIDがない場合は、CookieのプランID（ログイン処理中）を取得
    const cookie = loadCookie();
    if (cookie[SasCookieKeys.KEY_BEFORE_PURCHASE]) {
      processingPlanId = cookie[SasCookieKeys.KEY_BEFORE_PURCHASE];
    }
  }

  return {
    language: userState.language,
    country: userState.country,
    principal: principalState,
    isProcessing: isProcessing,
    processingPlanId: processingPlanId,
    glossary: Glossary.instance(glState)
  };
};

type SelectedState = ReturnType<typeof useStateProps>;
/**
 * StoreのStateとActionをdispatchする関数からComponentに渡すPropsへ
 */
const useMargeProps = (state: SelectedState, ownProps: OwnProps) => {
  const dispatch = useDispatch();
  const _isLoggedIn = state.principal.isLoggedIn;
  const services = ownProps.targetPlan ? ownProps.targetPlan.services : [];

  /** 初期化処理 */
  const onInit = async () => {
    const cookie = loadCookie();
    if (cookie[SasCookieKeys.KEY_BEFORE_PURCHASE]) {
      // 未ログイン状態で購入ボタンを押下し、ログインして遷移した場合
      deleteCookie(SasCookieKeys.KEY_BEFORE_PURCHASE);
      if (_isLoggedIn) {
        // 購入開始APIを呼び出し、購入確認画面を表示
        await dispatch(
          postPurchaseSession(
            cookie[SasCookieKeys.KEY_TOKEN],
            cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
            ownProps.targetPlan.planId,
            ownProps.targetPlan.name,
            state.country,
            state.language,
            ownProps.targetPlan.prices[0].payment
          )
        );
      }
    }
  };

  /** 購入ボタンクリック時の処理 */
  const onClick = async () => {
    if (!state.isProcessing) {
      if (_isLoggedIn) {
        // 購入開始APIを呼び、購入確認画面を表示
        // 購入確認画面のボタン押下時に呼び出すとSafariでブロックされるのでここで実施
        const cookie = loadCookie();
        await dispatch(
          postPurchaseSession(
            cookie[SasCookieKeys.KEY_TOKEN],
            cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
            ownProps.targetPlan.planId,
            ownProps.targetPlan.name,
            state.country,
            state.language,
            ownProps.targetPlan.prices[0].payment
          )
        );
      } else {
        // ログインした後に購入確認画面を表示できるようにクッキーに情報を格納
        saveCookie(
          SasCookieKeys.KEY_BEFORE_PURCHASE,
          ownProps.targetPlan.planId
        );
        dispatch(getLogin());
      }
    }
  };

  return {
    ...state,
    services: services,
    onInit: onInit,
    onClick: onClick
  };
};

export default function ContainerComponent(ownProps: OwnProps) {
  const _props = useMargeProps(useStateProps(), ownProps);
  const price = ownProps.targetPlan.prices.length
    ? ownProps.targetPlan.prices[0]
    : undefined;
  const isProcessingPurchase =
    _props.processingPlanId == ownProps.targetPlan.planId;
  return (
    <Services
      {..._props}
      taxIncludedPrice={price ? price.taxIncludedPrice : NaN}
      currency={price ? price.currency : ''}
      validityPeriod={ownProps.targetPlan.validityPeriod}
      perpetualValidityPeriod={ownProps.targetPlan.perpetualValidityPeriod}
      usageLimit={_props.services[0].usageLimit}
      staticLinks={ownProps.targetPlan.links}
      isProcessingPurchase={isProcessingPurchase}
    />
  );
}
